// Screen: "Home"
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getActiveGames, forfeitGame, getFirstWeekProgress } from '../apiClient';
import EloBadge, { getRankProgress } from '../components/EloBadge';
import Footer from '../components/Footer';

// Hardwood floor background style
const hardwoodFloorStyle = {
  background: `
    repeating-linear-gradient(90deg, #3d2817 0px, #4a3423 2px, #5c4033 4px, #4a3423 6px, #3d2817 8px),
    repeating-linear-gradient(0deg, transparent 0px, transparent 60px, rgba(0,0,0,0.15) 60px, rgba(0,0,0,0.15) 62px),
    linear-gradient(180deg, #4a3423 0%, #3d2817 50%, #2d1f12 100%)
  `,
  backgroundBlendMode: "overlay, overlay, normal"
};

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

function PageWatermark({ size = 400, opacity = 0.1, top = "50%", left = "50%" }) {
  return (
    <img src="/BuckEuchreLogo.png" alt="" style={{
      position: "fixed", top, left, transform: "translate(-50%, -50%)",
      width: size, height: "auto", opacity, pointerEvents: "none", zIndex: 0
    }} />
  );
}

// Jack playing card tile component
function JackCard({ title, description, suit, color, onClick, mobile, logo = '/BuckEuchreLogo.png' }) {
  const suitSymbols = { spade: '\u2660', heart: '\u2665', diamond: '\u2666', club: '\u2663' };
  const suitSymbol = suitSymbols[suit] || '\u2660';
  const isRed = suit === 'heart' || suit === 'diamond';
  const suitColor = isRed ? '#dc2626' : '#00090c';
  const w = mobile ? 140 : 160;
  const h = mobile ? 190 : 220;

  return (
    <button onClick={onClick} style={{
      background: '#fef9e7', border: '2px solid #d4af37', borderRadius: 8, padding: 0,
      width: w, height: h, textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
      color: '#222', position: 'relative', overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)', flexShrink: 0
    }}
      onMouseEnter={(e) => { if (!mobile) { e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)'; e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.4)'; }}}
      onMouseLeave={(e) => { if (!mobile) { e.currentTarget.style.transform = 'translateY(0) scale(1)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)'; }}}
    >
      <img src={logo} alt="" style={{
        position: 'absolute', top: '55%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 300, opacity: 0.15, pointerEvents: 'none'
      }} />
      <div style={{ position: 'absolute', top: 8, left: 10 }}>
        <span style={{ display: 'block', fontSize: mobile ? 14 : 16, fontWeight: 700, color: suitColor, lineHeight: 1 }}>J</span>
        <span style={{ display: 'block', fontSize: mobile ? 18 : 20, color: suitColor, lineHeight: 1 }}>{suitSymbol}</span>
      </div>
      <div style={{ position: 'absolute', bottom: 8, right: 10, transform: 'rotate(180deg)' }}>
        <span style={{ display: 'block', fontSize: mobile ? 14 : 16, fontWeight: 700, color: suitColor, lineHeight: 1 }}>J</span>
        <span style={{ display: 'block', fontSize: mobile ? 18 : 20, color: suitColor, lineHeight: 1 }}>{suitSymbol}</span>
      </div>
      <div style={{
        padding: mobile ? '45px 10px 16px' : '55px 12px 20px', position: 'relative', zIndex: 1,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: '100%', boxSizing: 'border-box'
      }}>
        <h3 style={{ fontSize: mobile ? 14 : 15, fontWeight: 700, marginBottom: 6, color, whiteSpace: 'pre-line', lineHeight: 1.3, textAlign: 'center' }}>{title}</h3>
        <p style={{ fontSize: mobile ? 10 : 11, color: '#666', margin: 0 }}>{description}</p>
      </div>
    </button>
  );
}

// Sub-item card for expanded groups — same size and style as JackCard
function SubCard({ title, description, suit, color, onClick, mobile, logo = '/BuckEuchreLogo.png' }) {
  const suitSymbols = { spade: '\u2660', heart: '\u2665', diamond: '\u2666', club: '\u2663' };
  const suitSymbol = suitSymbols[suit] || '\u2660';
  const isRed = suit === 'heart' || suit === 'diamond';
  const suitColor = isRed ? '#dc2626' : '#00090c';
  const w = mobile ? 140 : 160;
  const h = mobile ? 190 : 220;

  return (
    <button onClick={onClick} style={{
      background: '#fef9e7', border: '2px solid #d4af37', borderRadius: 8, padding: 0,
      width: w, height: h, textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
      color: '#222', position: 'relative', overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)', flexShrink: 0
    }}
      onMouseEnter={(e) => { if (!mobile) { e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)'; e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.4)'; }}}
      onMouseLeave={(e) => { if (!mobile) { e.currentTarget.style.transform = 'translateY(0) scale(1)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)'; }}}
    >
      <img src={logo} alt="" style={{
        position: 'absolute', top: '55%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 300, opacity: 0.15, pointerEvents: 'none'
      }} />
      <div style={{ position: 'absolute', top: 8, left: 10 }}>
        <span style={{ display: 'block', fontSize: mobile ? 14 : 16, fontWeight: 700, color: suitColor, lineHeight: 1 }}>J</span>
        <span style={{ display: 'block', fontSize: mobile ? 18 : 20, color: suitColor, lineHeight: 1 }}>{suitSymbol}</span>
      </div>
      <div style={{ position: 'absolute', bottom: 8, right: 10, transform: 'rotate(180deg)' }}>
        <span style={{ display: 'block', fontSize: mobile ? 14 : 16, fontWeight: 700, color: suitColor, lineHeight: 1 }}>J</span>
        <span style={{ display: 'block', fontSize: mobile ? 18 : 20, color: suitColor, lineHeight: 1 }}>{suitSymbol}</span>
      </div>
      <div style={{
        padding: mobile ? '45px 10px 16px' : '55px 12px 20px', position: 'relative', zIndex: 1,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: '100%', boxSizing: 'border-box'
      }}>
        <h3 style={{ fontSize: mobile ? 14 : 15, fontWeight: 700, marginBottom: 6, color, whiteSpace: 'pre-line', lineHeight: 1.3, textAlign: 'center' }}>{title}</h3>
        <p style={{ fontSize: mobile ? 10 : 11, color: '#666', margin: 0 }}>{description}</p>
      </div>
    </button>
  );
}

// Expandable group card
function GroupCard({ title, description, suit, color, expanded, onToggle, children, mobile }) {
  const suitSymbols = { spade: '\u2660', heart: '\u2665', diamond: '\u2666', club: '\u2663' };
  const suitSymbol = suitSymbols[suit] || '\u2660';
  const isRed = suit === 'heart' || suit === 'diamond';
  const suitColor = isRed ? '#dc2626' : '#00090c';
  const w = mobile ? 140 : 160;
  const h = mobile ? 190 : 220;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <button onClick={onToggle} style={{
        background: '#fef9e7', border: `2px solid ${expanded ? '#fbbf24' : '#d4af37'}`, borderRadius: 8, padding: 0,
        width: w, height: h, textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
        color: '#222', position: 'relative', overflow: 'hidden',
        boxShadow: expanded ? '0 6px 20px rgba(251,191,36,0.3)' : '0 4px 12px rgba(0,0,0,0.3)', flexShrink: 0
      }}
        onMouseEnter={(e) => { if (!mobile) { e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)'; e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.4)'; }}}
        onMouseLeave={(e) => { if (!mobile) { e.currentTarget.style.transform = 'translateY(0) scale(1)'; e.currentTarget.style.boxShadow = expanded ? '0 6px 20px rgba(251,191,36,0.3)' : '0 4px 12px rgba(0,0,0,0.3)'; }}}
      >
        <img src="/BuckEuchreLogo.png" alt="" style={{
          position: 'absolute', top: '55%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 300, opacity: 0.15, pointerEvents: 'none'
        }} />
        <div style={{ position: 'absolute', top: 8, left: 10 }}>
          <span style={{ display: 'block', fontSize: mobile ? 14 : 16, fontWeight: 700, color: suitColor, lineHeight: 1 }}>J</span>
          <span style={{ display: 'block', fontSize: mobile ? 18 : 20, color: suitColor, lineHeight: 1 }}>{suitSymbol}</span>
        </div>
        <div style={{ position: 'absolute', bottom: 8, right: 10, transform: 'rotate(180deg)' }}>
          <span style={{ display: 'block', fontSize: mobile ? 14 : 16, fontWeight: 700, color: suitColor, lineHeight: 1 }}>J</span>
          <span style={{ display: 'block', fontSize: mobile ? 18 : 20, color: suitColor, lineHeight: 1 }}>{suitSymbol}</span>
        </div>
        <div style={{
          padding: mobile ? '45px 10px 16px' : '55px 12px 20px', position: 'relative', zIndex: 1,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: '100%', boxSizing: 'border-box'
        }}>
          <h3 style={{ fontSize: mobile ? 14 : 15, fontWeight: 700, marginBottom: 6, color, whiteSpace: 'pre-line', lineHeight: 1.3, textAlign: 'center' }}>{title}</h3>
          <p style={{ fontSize: mobile ? 10 : 11, color: '#666', margin: 0 }}>{description}</p>
          <span style={{
            fontSize: 16, color: '#a0855b', marginTop: 6,
            transition: 'transform 0.2s',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            display: 'inline-block'
          }}>{'\u25BC'}</span>
        </div>
      </button>
      {/* Expanded sub-items */}
      {expanded && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
          gap: mobile ? 16 : 20, marginTop: mobile ? 12 : 16,
          padding: mobile ? '14px 8px' : '18px 14px',
          background: 'rgba(0,0,0,0.2)', borderRadius: 10,
          border: '1px solid rgba(212,175,55,0.2)',
          maxWidth: mobile ? 340 : 740
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, token, signout } = useAuth();
  const isMobile = useIsMobile();
  const [activeGames, setActiveGames] = useState([]);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showPlayerCountModal, setShowPlayerCountModal] = useState(false);
  const [firstWeekData, setFirstWeekData] = useState(null);

  // Redirect to sign-in if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/signin');
    }
  }, [isAuthenticated, navigate]);

  // Fetch active games and first week progress (re-fetches on every navigation to /home)
  useEffect(() => {
    if (isAuthenticated && token) {
      getActiveGames(token).then(data => {
        if (data.games && data.games.length > 0) {
          setActiveGames(data.games);
        }
      });
      getFirstWeekProgress(token).then(data => {
        if (data && !data.expired) setFirstWeekData(data);
      });
    }
  }, [isAuthenticated, token, location.key]);

  if (!isAuthenticated) return null;

  const pageContent = (
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '40px 32px',
      maxWidth: 900, margin: '0 auto', position: 'relative', zIndex: 1
    }}>
      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: isMobile ? 16 : 24 }}>
        <img
          src="/BuckEuchreLogoWhite.png"
          alt="Euchre Table"
          style={{
            width: isMobile ? 320 : 500,
            maxWidth: '90vw',
            height: 'auto',
            filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.5))'
          }}
        />
      </div>

      {/* User info bar (below logo so it's always visible) */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: isMobile ? 16 : 24,
        background: 'rgba(0,0,0,0.3)', borderRadius: 8,
        padding: isMobile ? '10px 14px' : '12px 20px',
        flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? 8 : 12
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flexWrap: 'wrap' }}>
          <span style={{ color: '#c9d1d9', fontSize: isMobile ? 13 : 14, fontWeight: 500 }}>
            {user?.nickname || 'Player'}
          </span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: '#1a2332', padding: isMobile ? '3px 8px' : '4px 12px', borderRadius: 12,
            fontSize: isMobile ? 11 : 12, fontWeight: 600, color: '#fbbf24',
            border: '1px solid #fbbf2440'
          }}>
            <img src="/bucks-icon.svg" alt="" style={{ width: 14, height: 14 }} />
            {(user?.playMoneyBalance || 100).toLocaleString()}
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <EloBadge cardRankId={user?.cardRankId || user?.permanentCardRank} suitSymbol={user?.suitSymbol || user?.preferredSuit || '♣'} points={user?.activeRP ?? user?.rankPoints ?? 0} rankName={user?.cardRankName || user?.rankName} showPoints={false} size="sm" badgeLabel="Your Rank" />
            {user?.nextSuitLevel && user?.activeToNextSuit > 0 && (
              <div style={{ width: isMobile ? 80 : 100 }}>
                <div style={{
                  background: '#21262d', borderRadius: 4, height: 5, overflow: 'hidden',
                  border: '1px solid #30363d'
                }}>
                  <div style={{
                    width: `${Math.round((user.suitLevelProgress ?? 0) * 100)}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #d4af3788, #d4af37)',
                    borderRadius: 4
                  }} />
                </div>
                <div style={{ fontSize: 9, color: '#8b949e', textAlign: 'center', marginTop: 2, whiteSpace: 'nowrap' }}>
                  {user.activeToNextSuit} RP → {user.nextSuitLevel}
                </div>
              </div>
            )}
          </div>
        </div>
        <button onClick={() => { signout(); navigate('/'); }} style={{
          background: 'transparent', border: '1px solid #30363d', color: '#8b949e',
          padding: isMobile ? '5px 10px' : '6px 14px', borderRadius: 6, cursor: 'pointer',
          fontSize: isMobile ? 12 : 13
        }}>
          Sign Out
        </button>
      </div>


      {/* First Week Challenge Banner */}
      {firstWeekData && !firstWeekData.expired && !firstWeekData.allComplete && (
        <div style={{
          background: 'linear-gradient(135deg, #1a2744, #1c3d2a)',
          border: '1px solid #3fb950',
          borderRadius: 10,
          padding: isMobile ? '14px 16px' : '16px 20px',
          marginBottom: isMobile ? 16 : 24
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ color: '#3fb950', fontWeight: 700, fontSize: isMobile ? 14 : 16 }}>
              First Week Challenge
            </span>
            <span style={{ color: '#8b949e', fontSize: 12 }}>
              {firstWeekData.daysRemaining} day{firstWeekData.daysRemaining !== 1 ? 's' : ''} left
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {firstWeekData.challenges.map(c => (
              <span key={c.id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: c.completed ? '#238636' : '#21262d',
                color: c.completed ? '#fff' : '#8b949e',
                padding: '4px 10px', borderRadius: 12,
                fontSize: 11, fontWeight: 500,
                border: c.completed ? 'none' : '1px solid #30363d'
              }}>
                {c.completed ? '\u2713' : '\u25CB'} {c.label}
              </span>
            ))}
          </div>
          <div style={{ marginTop: 8, color: '#8b949e', fontSize: 11 }}>
            {firstWeekData.challenges.filter(c => c.completed).length}/{firstWeekData.challenges.length} complete
            {' \u2022 '}25 Bucks each{' \u2022 '}Complete all 7 for Newcomer badge + 100 bonus!
          </div>
        </div>
      )}

      {/* Resume Game Modal */}
      {showResumeModal && activeGames.length > 0 && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShowResumeModal(false)}>
          <div style={{
            background: '#161b22', border: '1px solid #30363d', borderRadius: 12,
            padding: 28, maxWidth: 440, width: '90%'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ color: '#e2e8f0', fontSize: 20, marginBottom: 16 }}>Resume Game</h2>
            {activeGames.map(game => {
              const state = typeof game.gameState === 'string' ? JSON.parse(game.gameState) : game.gameState;
              return (
                <div key={game.gameId} style={{
                  background: '#0d1117', border: '1px solid #30363d', borderRadius: 8,
                  padding: '14px 18px', marginBottom: 10
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <span style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 600 }}>
                        {state?.playerCount || 3} Players
                      </span>
                      <span style={{ color: '#8b949e', fontSize: 12, marginLeft: 10 }}>
                        Hand {state?.handNumber || '?'} | {state?.wagerMode === 'none' ? 'Free' : 'Bucks'}
                      </span>
                    </div>
                    <span style={{ color: '#6b7280', fontSize: 10 }}>
                      {state?.savedAt ? new Date(state.savedAt).toLocaleDateString() : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => navigate(`/play?resume=${game.gameId}`)} style={{
                      flex: 1, background: '#238636', color: '#fff', border: 'none',
                      borderRadius: 6, padding: '8px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer'
                    }}>Resume</button>
                    <button onClick={() => {
                      if (window.confirm('Forfeit this game? You may lose your buy-in.')) {
                        forfeitGame(token, game.gameId).then(result => {
                          if (result.success) {
                            setActiveGames(prev => prev.filter(g => g.gameId !== game.gameId));
                          }
                        });
                      }
                    }} style={{
                      background: '#7f1d1d', color: '#ef4444', border: 'none',
                      borderRadius: 6, padding: '8px 14px', fontSize: 13, cursor: 'pointer'
                    }}>Forfeit</button>
                  </div>
                </div>
              );
            })}
            <button onClick={() => setShowResumeModal(false)} style={{
              display: 'block', width: '100%', marginTop: 8, background: '#4b5563', color: '#fff',
              border: 'none', borderRadius: 6, padding: '8px 0', fontSize: 13, cursor: 'pointer'
            }}>Close</button>
          </div>
        </div>
      )}

      {/* Player Count Selection Modal (desktop only) */}
      {showPlayerCountModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShowPlayerCountModal(false)}>
          <div style={{
            background: '#161b22', border: '1px solid #30363d', borderRadius: 12,
            padding: 32, maxWidth: 360, width: '90%', textAlign: 'center'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ color: '#e2e8f0', fontSize: 22, marginBottom: 8 }}>How Many Players?</h2>
            <p style={{ color: '#8b949e', fontSize: 13, marginBottom: 24 }}>Choose your table size</p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
              <button onClick={() => { setShowPlayerCountModal(false); navigate('/play?players=3'); }} style={{
                flex: 1, background: '#238636', color: '#fff', border: 'none',
                borderRadius: 8, padding: '18px 0', fontSize: 18, fontWeight: 700, cursor: 'pointer'
              }}>
                3 Players
                <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, marginTop: 4 }}>Classic</div>
              </button>
              <button onClick={() => { setShowPlayerCountModal(false); navigate('/play?players=4'); }} style={{
                flex: 1, background: '#7c3aed', color: '#fff', border: 'none',
                borderRadius: 8, padding: '18px 0', fontSize: 18, fontWeight: 700, cursor: 'pointer'
              }}>
                4 Players
                <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, marginTop: 4 }}>With 9s</div>
              </button>
            </div>
            <button onClick={() => setShowPlayerCountModal(false)} style={{
              display: 'block', width: '100%', marginTop: 14, background: 'transparent',
              border: '1px solid #30363d', color: '#8b949e', borderRadius: 6,
              padding: '8px 0', fontSize: 13, cursor: 'pointer'
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* RP Progress Card */}
      {user && (() => {
        const rp = getRankProgress(user.lifetimeRP ?? 0);
        return (
          <div style={{
            background: 'linear-gradient(135deg, #1c1a14, #2a2010)',
            border: '2px solid #d4af3760',
            borderRadius: 10,
            padding: isMobile ? '12px 16px' : '14px 20px',
            marginBottom: isMobile ? 14 : 20,
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 12 : 18
          }}>
            <EloBadge
              cardRankId={user.cardRankId || user.permanentCardRank}
              suitSymbol={user.suitSymbol || user.preferredSuit || '♠'}
              points={user.rankPoints ?? 0}
              rankName={user.cardRankName || user.rankName}
              showPoints={false}
              size="md"
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                <span style={{ color: '#d4af37', fontWeight: 700, fontSize: isMobile ? 13 : 15 }}>
                  {rp.rank.name}
                </span>
                <span style={{ color: '#8b949e', fontSize: isMobile ? 10 : 12 }}>
                  {(user.lifetimeRP ?? 0).toLocaleString()} RP
                </span>
              </div>
              <div style={{ background: '#21262d', borderRadius: 4, height: 8, overflow: 'hidden', border: '1px solid #30363d', marginBottom: 4 }}>
                <div style={{
                  width: `${Math.round(rp.progress * 100)}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #b8860b, #d4af37, #fbbf24)',
                  borderRadius: 4,
                  transition: 'width 0.4s ease'
                }} />
              </div>
              {rp.nextRank ? (
                <div style={{ fontSize: isMobile ? 10 : 11, color: '#8b949e' }}>
                  {rp.pointsToNext.toLocaleString()} RP to <span style={{ color: '#d4af37' }}>{rp.nextRank.name}</span>
                </div>
              ) : (
                <div style={{ fontSize: isMobile ? 10 : 11, color: '#d4af37', fontWeight: 600 }}>Max Rank!</div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Resume game banner */}
      {activeGames.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #1a2744, #1c3d2a)',
          border: '1px solid #f59e0b60', borderRadius: 10,
          padding: isMobile ? '10px 14px' : '12px 20px',
          marginBottom: isMobile ? 14 : 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8
        }}>
          <span style={{ color: '#f59e0b', fontSize: isMobile ? 13 : 14, fontWeight: 600 }}>
            {activeGames.length} game{activeGames.length > 1 ? 's' : ''} in progress
          </span>
          <button
            onClick={() => activeGames.length === 1 ? navigate(`/play?resume=${activeGames[0].gameId}`) : setShowResumeModal(true)}
            style={{ background: '#f59e0b', color: '#000', border: 'none', borderRadius: 6, padding: '6px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            Resume
          </button>
        </div>
      )}

      {/* 4 Big Action Tiles */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr',
        gap: isMobile ? 12 : 20,
        marginTop: isMobile ? 4 : 8,
        marginBottom: isMobile ? 16 : 24,
      }}>
        {[
          {
            title: 'Play Euchre',
            subtitle: 'Partner / Team Euchre',
            accentColor: '#0ea5e9',
            bgGradient: 'linear-gradient(160deg, #0c1a2e 0%, #0a2540 60%, #061830 100%)',
            borderColor: '#0ea5e960',
            logo: '/EuchreLogoWhite.png',
            logoWidth: [170, 230],
            onClick: () => navigate('/euchre'),
          },
          {
            title: 'Play Buck Euchre',
            subtitle: 'Wager bucks with AI',
            accentColor: '#22c55e',
            bgGradient: 'linear-gradient(160deg, #0c1f0c 0%, #0a2a12 60%, #061a08 100%)',
            borderColor: '#22c55e60',
            logo: '/BuckEuchreLogoWhite.png',
            logoWidth: [210, 280],
            onClick: () => isMobile ? navigate('/play?players=3') : setShowPlayerCountModal(true),
          },
          {
            title: '⚡ Live Play',
            subtitle: 'Jump in with real players',
            accentColor: '#d4af37',
            bgGradient: 'linear-gradient(160deg, #1a1408 0%, #2a2010 60%, #181208 100%)',
            borderColor: '#d4af3780',
            logo: '/EuchreLogoWhite.png',
            logoWidth: [170, 230],
            onClick: () => navigate('/live-play'),
          },
          {
            title: 'Community Room',
            subtitle: 'Play & chat online',
            accentColor: '#f43f5e',
            bgGradient: 'linear-gradient(160deg, #1f0c1a 0%, #2a0a1c 60%, #180614 100%)',
            borderColor: '#f43f5e60',
            logo: '/EuchreLogoWhite.png',
            logoWidth: [170, 230],
            onClick: () => navigate('/community'),
          },
          {
            title: 'My Profile',
            subtitle: 'Stats, rank & settings',
            accentColor: '#d4af37',
            bgGradient: 'linear-gradient(160deg, #1f1a0c 0%, #2a220a 60%, #181408 100%)',
            borderColor: '#d4af3760',
            logo: '/EuchreLogoWhite.png',
            logoWidth: [170, 230],
            onClick: () => navigate('/profile'),
          },
        ].map(({ title, subtitle, accentColor, bgGradient, borderColor, logo, logoWidth, onClick }) => (
          <button
            key={title}
            onClick={onClick}
            style={{
              background: bgGradient,
              border: `2px solid ${borderColor}`,
              borderRadius: isMobile ? 12 : 16,
              padding: 0,
              cursor: 'pointer',
              textAlign: 'left',
              color: '#e2e8f0',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.2s',
              height: isMobile ? 200 : 270,
              display: 'flex',
              flexDirection: 'column',
            }}
            onMouseEnter={e => { if (!isMobile) { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 12px 32px ${borderColor}`; } }}
            onMouseLeave={e => { if (!isMobile) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; } }}
          >
            {/* Logo area — fills top portion of tile */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '14px 12px 6px' : '20px 16px 8px' }}>
              <img src={logo} alt="" style={{ width: isMobile ? logoWidth[0] : logoWidth[1], height: 'auto', objectFit: 'contain', opacity: 0.9, pointerEvents: 'none' }} />
            </div>
            {/* Text strip — always below logo, never overlapping */}
            <div style={{
              background: 'rgba(0,0,0,0.45)',
              borderTop: `1px solid ${borderColor}`,
              padding: isMobile ? '10px 14px' : '12px 18px',
              flexShrink: 0,
            }}>
              <div style={{ fontSize: isMobile ? 14 : 18, fontWeight: 800, color: accentColor, lineHeight: 1.1 }}>{title}</div>
              <div style={{ fontSize: isMobile ? 10 : 12, color: '#94a3b8', marginTop: 2 }}>{subtitle}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Secondary link groups */}
      {[
        {
          heading: 'My Profile',
          color: '#d4af37',
          links: [
            { label: 'Profile & Trophies', path: '/profile' },
            { label: 'Analytics', path: '/analytics' },
            { label: 'Membership', path: '/membership' },
            { label: 'Friends', path: '/friends' },
            { label: 'Shop', path: '/shop' },
            { label: 'Invite Friends', path: '/referrals' },
            { label: 'Earn Bucks', path: '/fund' },
          ],
        },
        {
          heading: 'Community',
          color: '#0ea5e9',
          links: [
            { label: 'Leaderboards', path: '/leaderboards' },
            { label: 'Clubs', path: '/clubs' },
            { label: 'Seasons', path: '/seasons' },
            { label: 'Tournaments', path: '/tournaments' },
            { label: 'Leaders', path: '/leaders' },
            { label: 'Regional Chat', path: '/regional-chat' },
            { label: 'Fairness', path: '/fairness' },
          ],
        },
        {
          heading: 'Learn',
          color: '#a78bfa',
          links: [
            { label: 'Tutorial', path: '/tutorial' },
          ],
        },
      ].map(({ heading, color, links }) => (
        <div key={heading} style={{ marginBottom: isMobile ? 16 : 20 }}>
          <div style={{ fontSize: isMobile ? 11 : 12, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, paddingLeft: 2 }}>{heading}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 7 : 10 }}>
            {links.map(({ label, path }) => (
              <button key={label} onClick={() => navigate(path)} style={{
                background: 'rgba(255,255,255,0.05)', border: '1px solid #30363d',
                color: '#8b949e', borderRadius: 20, padding: isMobile ? '5px 12px' : '6px 16px',
                fontSize: isMobile ? 11 : 12, cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#c9d1d9'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#8b949e'; }}
              >{label}</button>
            ))}
          </div>
        </div>
      ))}

      {/* Admin links */}
      {user?.isAdmin && (
        <div style={{ marginBottom: isMobile ? 16 : 20 }}>
          <div style={{ fontSize: isMobile ? 11 : 12, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, paddingLeft: 2 }}>Admin</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 7 : 10 }}>
            {[
              { label: 'Admin Panel', path: '/admin' },
              { label: 'AI Stats', path: '/ai-stats' },
              { label: 'AI Sim Lab', path: '/ai-simulation' },
              { label: 'Game Viewer', path: '/game-viewer' },
              { label: 'BG Lab', path: '/background-lab' },
            ].map(({ label, path }) => (
              <button key={label} onClick={() => navigate(path)} style={{
                background: 'rgba(239,68,68,0.08)', border: '1px solid #ef444430',
                color: '#ef4444', borderRadius: 20, padding: isMobile ? '5px 12px' : '6px 16px',
                fontSize: isMobile ? 11 : 12, cursor: 'pointer', fontFamily: 'inherit'
              }}>{label}</button>
            ))}
          </div>
        </div>
      )}
      <Footer isMobile={isMobile} />
    </div>
  );

  // Mobile: fixed outer + scrollable inner (for Capacitor scrollEnabled:false)
  if (isMobile) {
    return (
      <div style={{ ...hardwoodFloorStyle, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
        <div style={{
          height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch'
        }}>
          {pageContent}
        </div>
      </div>
    );
  }

  // Desktop: normal scrollable page
  return (
    <div style={{ ...hardwoodFloorStyle, minHeight: '100vh', position: 'relative' }}>
      <PageWatermark size={500} opacity={0.08} />
      {pageContent}
    </div>
  );
}
